/**
 * Dashboard "Queue job" dialog.
 *
 * Workflows come from `GET /api/workflows`; entity names from
 * `GET /api/entities`. The entity list is derived from the workflows the
 * caller can see, so an entity with nothing to run is never offered.
 * Queueing rules and error copy live in `./queueJob`.
 *
 * Loading / empty / error / permission-denied states per Rule Zero #8.
 */
import { useEffect, useMemo, useRef } from "react";
import { AlertCircle, Lock, X } from "lucide-react";

import { QueueJobForm } from "@/features/dashboard/QueueJobForm";
import { availability, describeLoadError } from "@/features/dashboard/queueJob";
import { useWorkflows } from "@/features/workflows/api";
import { useEntities } from "@/lib/hooks/useEntities";

interface Props {
  open: boolean;
  onClose: () => void;
  onStarted?: (jobId: string) => void;
}

const SECONDARY_BTN =
  "rounded-sm border border-paper-line-strong bg-paper-3 px-3 py-1.5 text-sm text-ink-1 hover:bg-paper-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500";

export function QueueJobModal({ open, onClose, onStarted }: Props) {
  // Mounting only while open means no request is made until the dialog
  // is used, and every open starts with nothing selected.
  if (!open) return null;
  return <QueueJobDialog onClose={onClose} onStarted={onStarted} />;
}

function QueueJobDialog({ onClose, onStarted }: Omit<Props, "open">) {
  const workflowsQuery = useWorkflows();
  const entitiesQuery = useEntities();
  const dialogRef = useRef<HTMLDivElement>(null);

  // Focus once on open. Kept apart from the key handler so a new
  // `onClose` identity (the dashboard re-renders every 2s) cannot pull
  // focus out of a select the user is working in.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const workflows = useMemo(() => workflowsQuery.data?.items ?? [], [workflowsQuery.data]);
  const entityNames = useMemo(
    () => new Map((entitiesQuery.data?.items ?? []).map((e) => [e.id, e.full])),
    [entitiesQuery.data],
  );
  const hasQueueable = workflows.some((w) => availability(w).ok);
  const loadError = workflowsQuery.isError ? describeLoadError(workflowsQuery.error) : null;

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center bg-ink-1/40 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="queue-job-title"
        tabIndex={-1}
        className="w-full max-w-md rounded-lg border border-paper-line bg-paper-3 shadow-xl focus:outline-none"
      >
        <header className="flex items-center gap-2 border-b border-paper-line p-4">
          <h3 id="queue-job-title" className="flex-1 text-md font-medium text-ink-1">
            Queue a job
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-sm p-1 text-ink-3 hover:bg-paper-tint focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        {workflowsQuery.isPending ? (
          <>
            <LoadingSkeleton />
            <CloseFooter onClose={onClose} />
          </>
        ) : loadError ? (
          <>
            <div className="p-4">
              <div
                role="alert"
                className="flex items-start gap-3 rounded-md border border-status-failed/40 bg-status-failed-bg p-3 text-sm"
              >
                {loadError.kind === "denied" ? (
                  <Lock size={16} className="mt-0.5 shrink-0 text-ink-3" aria-hidden />
                ) : (
                  <AlertCircle size={16} className="mt-0.5 shrink-0 text-status-failed" aria-hidden />
                )}
                <div>
                  <div className="text-ink-1">{loadError.message}</div>
                  {loadError.correlationId && (
                    <div className="mt-1 font-mono text-[10px] text-ink-3">
                      Reference: {loadError.correlationId}
                    </div>
                  )}
                  {loadError.kind === "error" && (
                    <button
                      type="button"
                      onClick={() => workflowsQuery.refetch()}
                      className={`mt-2 ${SECONDARY_BTN}`}
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>
            </div>
            <CloseFooter onClose={onClose} />
          </>
        ) : !hasQueueable ? (
          <>
            <div className="p-4">
              <div className="rounded-md border border-dashed border-paper-line bg-paper-2 p-8 text-center text-sm text-ink-3">
                <p className="text-ink-2">No workflows can be queued from here.</p>
                <p className="mt-1 text-xs">
                  {workflows.length === 0
                    ? "No workflows exist for your entities yet."
                    : "The workflows you can see are paused, retired, or need inputs this dialog cannot collect."}
                </p>
              </div>
            </div>
            <CloseFooter onClose={onClose} />
          </>
        ) : (
          <QueueJobForm
            workflows={workflows}
            entityNames={entityNames}
            onClose={onClose}
            onStarted={onStarted}
          />
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4 p-4" role="status" aria-busy="true">
      <span className="sr-only">Loading workflows</span>
      <div className="h-14 animate-pulse rounded-md bg-paper-tint" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-1">
          <div className="h-3 w-16 animate-pulse rounded-sm bg-paper-tint" />
          <div className="h-9 animate-pulse rounded-sm bg-paper-tint" />
        </div>
      ))}
    </div>
  );
}

function CloseFooter({ onClose }: { onClose: () => void }) {
  return (
    <footer className="flex items-center justify-end border-t border-paper-line p-4">
      <button type="button" onClick={onClose} className={SECONDARY_BTN}>
        Close
      </button>
    </footer>
  );
}
